import { useCallback, useEffect, useMemo, useState } from 'react';
import { PeriodOption } from '../services/api';
import { getServerChartPeriodRange } from '../utils/chartPeriodRange';
import {
  addServerCalendarDays,
  endOfServerDay,
  getServerAdjustedNow,
  startOfServerDay,
  subscribeServerTime,
} from '../utils/serverTime';

/** datetime-local value from a Date (local wall components of that Date object). */
export function formatDayRangeForCustomInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Período / rango custom / navegación día — misma semántica que Shelly DeviceModal.
 */
export function useDeviceChartPeriod(initialPeriod: PeriodOption = 'today') {
  const [period, setPeriod] = useState<PeriodOption>(initialPeriod);
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);
  const [customStartInput, setCustomStartInput] = useState('');
  const [customEndInput, setCustomEndInput] = useState('');
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [timeRev, setTimeRev] = useState(0);

  useEffect(() => subscribeServerTime((ev) => {
    if (ev.offsetCorrected) setTimeRev((n) => n + 1);
  }), []);

  const range = useMemo(() => {
    void timeRev;
    return getServerChartPeriodRange(period, customStart, customEnd);
  }, [period, customStart, customEnd, timeRev]);

  const handlePeriodChange = useCallback((p: PeriodOption) => {
    setPeriod(p);
    if (p === 'custom') {
      const now = getServerAdjustedNow();
      const start = customStart || startOfServerDay(now);
      const end = customEnd || now;
      setCustomStartInput(formatDayRangeForCustomInput(start));
      setCustomEndInput(formatDayRangeForCustomInput(end));
      setCustomDateDialogOpen(true);
      return;
    }
    if (p === 'today') {
      setCustomStart(null);
      setCustomEnd(null);
      setCustomStartInput('');
      setCustomEndInput('');
    }
  }, [customStart, customEnd]);

  const handleCustomDateConfirm = useCallback(() => {
    if (!customStartInput || !customEndInput) return;
    const start = new Date(customStartInput);
    const end = new Date(customEndInput);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) return;
    setCustomStart(start);
    setCustomEnd(end);
    setPeriod('custom');
    setCustomDateDialogOpen(false);
  }, [customStartInput, customEndInput]);

  const handleCustomDateCancel = useCallback(() => {
    setCustomDateDialogOpen(false);
    if (!customStart || !customEnd) {
      setPeriod('today');
      setCustomStartInput('');
      setCustomEndInput('');
    }
  }, [customStart, customEnd]);

  const navigateDay = useCallback((direction: 'previous' | 'next') => {
    const todayStart = startOfServerDay(getServerAdjustedNow());
    let targetDate: Date;
    if (direction === 'previous') {
      if (period === 'custom' && customStart) {
        targetDate = addServerCalendarDays(startOfServerDay(customStart), -1);
      } else {
        targetDate = addServerCalendarDays(todayStart, -1);
      }
    } else {
      if (period === 'custom' && customStart) {
        targetDate = addServerCalendarDays(startOfServerDay(customStart), 1);
      } else {
        return;
      }
    }
    if (direction === 'next' && targetDate.getTime() > todayStart.getTime()) return;
    const startDate = startOfServerDay(targetDate);
    let endDate = endOfServerDay(targetDate);
    const now = getServerAdjustedNow();
    if (endDate > now) endDate = now;
    setCustomStart(startDate);
    setCustomEnd(endDate);
    setCustomStartInput(formatDayRangeForCustomInput(startDate));
    setCustomEndInput(formatDayRangeForCustomInput(endDate));
    setPeriod('custom');
  }, [period, customStart]);

  const goToday = useCallback(() => {
    setPeriod('today');
    setCustomStart(null);
    setCustomEnd(null);
    setCustomStartInput('');
    setCustomEndInput('');
  }, []);

  const canNavigateNextDay =
    period === 'custom'
    && !!customStart
    && startOfServerDay(customStart).getTime() < startOfServerDay(getServerAdjustedNow()).getTime();

  const isTodayActive = period === 'today' && !customStart;

  return {
    period,
    setPeriod,
    customStart,
    customEnd,
    range,
    customStartInput,
    setCustomStartInput,
    customEndInput,
    setCustomEndInput,
    customDateDialogOpen,
    setCustomDateDialogOpen,
    handlePeriodChange,
    handleCustomDateConfirm,
    handleCustomDateCancel,
    navigateDay,
    goToday,
    canNavigateNextDay,
    isTodayActive,
  };
}
